/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, September 23rd 2021, 12:24:20 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import * as React from "react";
import { findIndex } from "lodash";

/* Added by Eugene */
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { Link } from "react-router-dom";
import { EXCLUDED_URLS } from "core/routes";

/* Feather Icons */
import {
  Home,
  Globe,
  Folder,
  Users,
  // Settings,
  DollarSign,
  LogOut,
  Briefcase,
  Database,
  MoreHorizontal,
} from "react-feather";

import Grid from "@mui/material/Grid";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslatedText } from "core";
import { useGlobalScope } from "core/providers";
import { URLS } from "core/routes";
import { compareURLPaths, resolveURLName } from "core/routes/utils";
import { OrganizationScopeSelector, OrganizationSwitcherButtonSelector } from "organizations";
import { Image } from "ui-widgets";

type IMenuItem = {
  name: string;
  icon: React.ReactNode;
  url: string;
  submenus?: ISubMenu[];
  onClick?: () => void;
  className?: string;
  description?: string;
  checkPermission?: () => boolean;
};

type ISubMenu = {
  section: string;
  checkPermission?: () => boolean;
  items: IMenuItem[];
};

const OSDrawerMenus = ({ toggleDrawerOpen = null, drawerOpen = true }) => {
  const location = useLocation();

  const LANG = useTranslatedText();

  const { logoutUser, loggedInUser } = useGlobalScope().userContext;

  const handleLogout = () => logoutUser();

  const topMenus = [
    {
      name: LANG.home,
      icon: <Home />,
      url: URLS.dashboard,
    },
    {
      name: LANG.myOrganization,
      icon: <Briefcase />,
      url: URLS.editSelectedOrganization,
    },
    { name: LANG.portals, icon: <Globe />, url: URLS.portals },
    {
      name: LANG.services,
      icon: <Folder />,
      url: URLS.services,
    },
    {
      name: LANG.databanks,
      icon: <Database />,
      url: URLS.databanks,
    },
    {
      name: LANG.more,
      icon: <MoreHorizontal />,
      url: "#",
      submenus: [
        {
          section: LANG.forms,
          items: [
            {
              name: LANG.manageForms,
              description: LANG.manageFormsDescription,
              url: URLS.formsList,
              icon: <Image src="/img/form.png" height={30} width={30} />,
            },
          ],
        },
        {
          section: LANG.profile,
          items: [
            {
              name: LANG.accountProfile,
              description: LANG.accountProfileDescription,
              url: resolveURLName("viewWorker", { workerId: loggedInUser.id }),
              icon: <Image src="/img/user.png" height={30} width={30} />,
            },
          ],
        },
        {
          section: LANG.payments,
          items: [
            {
              name: LANG.managePaymentMethods,
              description: LANG.managePaymentMethodsDescription,
              url: resolveURLName("organizationPaymentMethods"),
              icon: <Image src="/img/credit-card.png" height={30} width={30} />,
            },
          ],
        },
        {
          section: LANG.mediaLibrary,
          items: [
            {
              name: LANG.manageMediaLibrary,
              description: LANG.manageMediaLibraryDescription,
              url: resolveURLName("mediaLibrary"),
              icon: <Image src="/img/picture.png" height={30} width={30} />,
            },
          ],
        },
        {
          section: LANG.translations,
          checkPermission: () => loggedInUser?.is_superuser,
          items: [
            {
              name: LANG.manageTranslations,
              description: LANG.manageTranslationsDescription,
              url: resolveURLName("translations"),
              icon: <Image src="/img/translation.png" height={30} width={30} />,
            },
          ],
        },
        {
          section: LANG.integrations,
          checkPermission: () => loggedInUser?.is_superuser,
          items: [
            {
              name: LANG.manageIntegrations,
              description: LANG.manageIntegrationsDescription,
              url: resolveURLName("integrations"),
              icon: <Image src="/img/integration.png" height={30} width={30} />,
            },
          ],
        },
      ],
    },
  ];

  const bottomMenus = [
    {
      name: LANG.teams,
      icon: <Users />,
      submenus: [LANG.users, LANG.groups, LANG.departments],
      className: "bm-user",
      url: URLS.allTeams,
    },
    // {
    //   name: LANG.accountProfile,
    //   icon: <Settings />,
    //   url: resolveURLName("viewWorker", { workerId: loggedInUser.id }),
    //   className: "bm-settings",
    //   submenus: [],
    // },
    {
      name: LANG.billing,
      icon: <DollarSign />,
      url: URLS.billing,
      className: "bm-billing",
      submenus: [],
    },
    {
      name: LANG.logout,
      icon: <LogOut />,
      url: "#",
      className: "bm-logout",
      submenus: [],
      onClick: handleLogout,
    },
  ];

  EXCLUDED_URLS.forEach((url) => {
    const billingMenuIndex = findIndex(bottomMenus, { url });
    bottomMenus.splice(billingMenuIndex, 1);
  });

  return (
    <React.Fragment>
      <div className="second-side-bar pull-to-the-top">
        <Paper className="second-side-bar-in" elevation={0}>
          <Link className="brand-wrapper-link" to={URLS.dashboard}>
            <div className="brand-wrapper">
              <img className="image-style-A sidebar-logo" src="/img/onestop-logo-minus-symbol.svg" alt="Logo" />
            </div>
          </Link>
          <IconButton
            className={`toggle-sidebar-button ${!drawerOpen && "close"}`}
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={() => toggleDrawerOpen && toggleDrawerOpen()}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <div className="mobile-only-logo">
            <div className="brand-wrapper-symbol">
              <img className="image-style-A sidebar-logo" src="/img/onestop-full-logo.svg" alt="Onestop" />
            </div>
          </div>
        </Paper>

        <div className="sidebar-options-A">
          <OrganizationScopeSelector
            renderSelectorComponent={({ selectedOrganization, switchOrganization }) => (
              <OrganizationSwitcherButtonSelector
                switchOrganization={switchOrganization}
                selectedOrganization={selectedOrganization}
              />
            )}
          />
        </div>

        <MenuList className="app-primary-nav nav-spacing">
          {topMenus.map((menu, index) => {
            let menuNode = (
              <Tooltip title={menu.name} placement="right" arrow key={`tooltip-${index}-${menu.url.toString()}`}>
                <MenuItem
                  key={`${index}-${menu.url.toString()}`}
                  component={Link}
                  to={menu.url}
                  selected={compareURLPaths(menu.url, location?.pathname.split("/")?.[1])}
                >
                  <ListItemIcon className="app-primary-nav-icon" key={`${index}-top-menu-icon`}>
                    {menu.icon}
                  </ListItemIcon>
                  <ListItemText className="app-primary-nav-text" key={`${index}-top-menu-text`}>
                    {menu.name}
                  </ListItemText>
                </MenuItem>
              </Tooltip>
            );

            if (menu.submenus?.length > 0) {
              menuNode = <SubMenusPopOver submenus={menu.submenus} anchorNode={menuNode} />;
            }

            return menuNode;
          })}
        </MenuList>
      </div>

      <MenuList className="app-primary-nav second-side-bar nav-spacing" sx={{ marginTop: "auto" }}>
        {bottomMenus.map((menu, index) => (
          <Tooltip title={menu.name} placement="right" arrow key={`tooltip-${index}-${menu.url.toString()}`}>
            <MenuItem
              key={`${index}-${menu.url.toString()}`}
              component={Link}
              to={menu.url}
              selected={compareURLPaths(menu.url, location?.pathname.split("/")?.[1])}
              onClick={menu.onClick}
              className={menu.className}
            >
              <ListItemIcon className="app-primary-nav-icon" key={`${index}-bottom-menu-icon`}>
                {menu.icon}
              </ListItemIcon>
              <ListItemText className="app-primary-nav-text" key={`${index}-bottom-menu-text`}>
                {menu.name}
              </ListItemText>
              {menu.submenus.map((submenu, sub_menu_index) => (
                <div key={sub_menu_index}></div>
              ))}
            </MenuItem>
          </Tooltip>
        ))}
      </MenuList>
    </React.Fragment>
  );
};

export default OSDrawerMenus;

interface SubMenusPopOverProps {
  submenus: ISubMenu[];
  anchorNode: React.ReactNode;
}

const SubMenusPopOver = ({ submenus = [], anchorNode }: SubMenusPopOverProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigate = useNavigate();

  return (
    <>
      {/* eslint-disable-next-line */}
      <a onClick={handleClick} href="#">
        {anchorNode}
      </a>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{ sx: { maxWidth: "50%", padding: "10px" } }}
      >
        <MenuList className="user-popup" dense>
          <Grid container rowGap={4}>
            {submenus.map((submenu, index) => {
              if (submenu.checkPermission?.() === false) return null;
              return (
                <Grid key={`${index}`} item xs={12} md={6}>
                  <Typography variant="h6">{submenu.section}</Typography>
                  {submenu.items.map((item, index) => {
                    if (item.checkPermission?.() === false) return null;
                    return (
                      <MenuItem
                        key={item.url}
                        onClick={() => {
                          navigate(item.url);
                          handleClose();
                        }}
                      >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <Stack>
                          <Typography variant="h6" color="text.secondary">
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ textWrap: "wrap" }}>
                            {item.description}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    );
                  })}
                </Grid>
              );
            })}
          </Grid>
        </MenuList>
      </Popover>
    </>
  );
};
